import React from 'react';
import { apiOrigin } from './api';

// Resolve a Markdown image src: /api/… assets hang off the backend origin (matters in dev where
// the API is on another port); everything else (data:, http…) is left as-is.
const resolveImgSrc = (src) => (src && src.startsWith('/api/') ? apiOrigin + src : src);

// A small, dependency-free Markdown renderer for task descriptions & comments.
// Supports: # headings, **bold**, *italic*/_italic_, `code`, [links](url),
// ordered/unordered lists (with nesting by indentation), paragraphs & line breaks.
// It builds React elements (no dangerouslySetInnerHTML), so text is always escaped.

const ITEM_RE = /^(\s*)([-*+]|\d+\.)\s+(.+)$/;

// Inline formatting → array of React nodes.
//
// ── Everything in here is keyed, and keyed on where it came from ────────────────────────────────
//
// This returns an **array**, and React reconciles an array child-by-child against the one before
// it. Two things about the first version of this made that unsafe, and they compounded:
//
// **The plain text runs had no keys at all.** A mixed array — some children keyed, some not — is
// matched partly by key and partly by position, and the two disagree the moment a run of text
// changes length. React then moves a node relative to a sibling it believes is still there.
//
// **And the keys were the output position** (`out.length`), so the *same* `<strong>` was key 1 on
// one render and key 3 on the next the instant a word was added before it. Nothing is being
// re-identified there; it is being destroyed and recreated, and its neighbours renumbered under it.
//
// Together those produce `insertBefore: the node before which the new node is to be inserted is not
// a child of this node` — the error that took the Requests screen down, on the one view that draws
// descriptions through here.
//
// So: every child is keyed, text included, and the key is the **offset in the source string**. That
// is stable for the same input, unique within the array, and unchanged by what happens before it.
function inlineParse(text) {
  const out = [];
  // Where in the original string the next chunk starts, which is what the keys are built from.
  let at = 0;
  const push = (node) => { out.push(node); };
  const textAt = (str, pos) => <React.Fragment key={`t${pos}`}>{str}</React.Fragment>;
  const patterns = [
    { name: 'code', re: /`([^`]+)`/g },
    { name: 'bold', re: /\*\*([^*]+?)\*\*/g },
    { name: 'image', re: /!\[([^\]]*)\]\(([^)\s]+)\)/g },
    { name: 'link', re: /\[([^\]]+)\]\(([^)\s]+)\)/g },
    { name: 'em', re: /(\*|_)([^*_]+?)\1/g },
  ];
  let rest = String(text);
  let guard = 0;
  while (rest && guard++ < 2000) {
    let best = null;
    for (const p of patterns) {
      p.re.lastIndex = 0;
      const m = p.re.exec(rest);
      if (m && (!best || m.index < best.m.index)) best = { p, m };
    }
    if (!best) { push(textAt(rest, at)); break; }
    const { p, m } = best;
    if (m.index > 0) push(textAt(rest.slice(0, m.index), at));
    // The offset of the match itself, so the element keeps its key however the text around it
    // changes — which is the whole point of a key.
    const key = `${p.name}${at + m.index}`;
    if (p.name === 'code') push(<code key={key}>{m[1]}</code>);
    else if (p.name === 'bold') push(<strong key={key}>{inlineParse(m[1])}</strong>);
    else if (p.name === 'image') push(<img key={key} src={resolveImgSrc(m[2])} alt={m[1] || 'image'} className="md-img" />);
    else if (p.name === 'link') push(<a key={key} href={m[2]} target="_blank" rel="noreferrer">{m[1]}</a>);
    else if (p.name === 'em') push(<em key={key}>{m[2]}</em>);
    at += m.index + m[0].length;
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
}

const indentOf = (line) => ((line.match(ITEM_RE) || [])[1] || '').replace(/\t/g, '  ').length;

// Build a nested list tree from consecutive list lines (indentation = nesting).
function buildListTree(listLines) {
  const root = { ordered: /\d/.test((listLines[0].match(ITEM_RE) || [])[2] || '-'), items: [] };
  const stack = [{ indent: indentOf(listLines[0]), list: root }];
  for (const ln of listLines) {
    const m = ln.match(ITEM_RE);
    if (!m) continue;
    const indent = m[1].replace(/\t/g, '  ').length;
    const ordered = /\d/.test(m[2]);
    const text = m[3];
    let top = stack[stack.length - 1];
    if (indent > top.indent) {
      const parentItem = top.list.items[top.list.items.length - 1];
      if (parentItem) {
        const child = { ordered, items: [] };
        parentItem.children = child;
        stack.push({ indent, list: child });
        top = stack[stack.length - 1];
      }
    } else {
      while (stack.length > 1 && indent < top.indent) { stack.pop(); top = stack[stack.length - 1]; }
    }
    top.list.items.push({ text, children: null });
  }
  return root;
}

function renderList(node, key) {
  const Tag = node.ordered ? 'ol' : 'ul';
  return (
    <Tag key={key} className="md-list">
      {node.items.map((it, idx) => (
        <li key={idx}>{inlineParse(it.text)}{it.children && renderList(it.children, 'c' + idx)}</li>
      ))}
    </Tag>
  );
}

function parseBlocks(text) {
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) { blocks.push({ type: 'h', level: h[1].length, text: h[2] }); i++; continue; }
    if (ITEM_RE.test(line)) {
      const listLines = [];
      while (i < lines.length) {
        if (ITEM_RE.test(lines[i])) { listLines.push(lines[i]); i++; continue; }
        // Keep a single blank line inside the list if items continue after it.
        if (!lines[i].trim()) {
          let j = i + 1;
          while (j < lines.length && !lines[j].trim()) j++;
          if (j < lines.length && ITEM_RE.test(lines[j])) { i = j; continue; }
        }
        break;
      }
      blocks.push({ type: 'list', listLines });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^#{1,6}\s+/.test(lines[i]) && !ITEM_RE.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push({ type: 'p', para });
  }
  return blocks;
}

export default function Markdown({ text, children, className = '' }) {
  // Written to take a `text` prop; called once as `<Markdown>{body}</Markdown>`, which left `text`
  // undefined and rendered an empty box with no error anywhere. Both forms work now, because a
  // renderer that silently draws nothing costs more to find than one that is easy to call.
  const body = text != null && text !== '' ? text
    : (Array.isArray(children) ? children.filter(c => typeof c === 'string').join('') : children);
  if (!body || typeof body !== 'string') return null;
  const blocks = parseBlocks(body);
  return (
    <div className={`markdown ${className}`}>
      {blocks.map((b, idx) => {
        if (b.type === 'h') {
          const lvl = Math.min(b.level, 4);
          return <div key={idx} className={`md-h md-h${lvl}`}>{inlineParse(b.text)}</div>;
        }
        if (b.type === 'list') return renderList(buildListTree(b.listLines), idx);
        return (
          <p key={idx} className="md-p">
            {b.para.map((l, j) => (
              <React.Fragment key={j}>{j > 0 && <br />}{inlineParse(l)}</React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
